import React, { useMemo } from 'react';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis,
  PolarRadiusAxis, Radar
} from 'recharts';
import { TrendingUp, TrendingDown, Minus, CheckCircle, AlertTriangle, XCircle } from 'lucide-react';

const CATEGORIES = { general: 'General & DMA', environmental: 'Environmental', social: 'Social', governance: 'Governance' };
const COLORS = { general: '#10b981', environmental: '#22c55e', social: '#3b82f6', governance: '#a855f7' };

const ReviewStep = ({ assessmentData, questions, onNext, submitLabel }) => {
  const answers = assessmentData?.answers || [];
  const scores = assessmentData?.category_scores || {};
  const totalScore = assessmentData?.total_score || 0;
  const recommendations = assessmentData?.recommendations || [];
  const company = assessmentData?.company_info || {};
  
  const totalQuestions = questions?.length || 0;
  const answeredCount = answers.length;
  const allAnswered = answeredCount >= totalQuestions;

  const qMap = useMemo(() => {
    const m = {};
    for (const q of (questions || [])) m[q.id] = q;
    return m;
  }, [questions]);

  const answeredByCategory = useMemo(() => {
    const result = {};
    for (const ans of answers) {
      const q = qMap[ans.question_id];
      if (!q) continue;
      const cat = q.category || 'general';
      if (!result[cat]) result[cat] = [];
      const opt = q.answer_options?.find(o => o.id === ans.answer_option_id);
      result[cat].push({ question: q, answer: ans, optionName: opt?.name || ans.status || 'Answered', isNa: opt?.is_na, points: opt?.points ?? 0, maxPoints: q.points || 0 });
    }
    return result;
  }, [answers, qMap]);

  const answerStats = useMemo(() => {
    let yes = 0, partial = 0, no = 0, na = 0;
    for (const ans of answers) {
      const q = qMap[ans.question_id];
      if (!q) continue;
      const opt = q.answer_options?.find(o => o.id === ans.answer_option_id);
      if (!opt) continue;
      if (opt.is_na) na++;
      else if (opt.name?.toLowerCase() === 'yes') yes++;
      else if (opt.name?.toLowerCase().includes('partial')) partial++;
      else no++;
    }
    return { yes, partial, no, na };
  }, [answers, qMap]);

  const pieData = Object.entries(CATEGORIES).map(([key, label]) => ({
    name: label, value: parseFloat((scores[key] || 0).toFixed(1)), color: COLORS[key]
  })).filter(d => d.value > 0 || true);

  const barData = Object.entries(CATEGORIES).map(([key, label]) => ({
    name: label.split(' ')[0], score: parseFloat((scores[key] || 0).toFixed(1)), fill: COLORS[key]
  }));

  const radarData = Object.entries(CATEGORIES).map(([key, label]) => ({
    category: label.replace(' & DMA', ''), score: parseFloat((scores[key] || 0).toFixed(1)), fullMark: 100
  }));

  const answerDistData = [
    { name: 'Yes', value: answerStats.yes, color: '#22c55e' },
    { name: 'Partial', value: answerStats.partial, color: '#f59e0b' },
    { name: 'No', value: answerStats.no, color: '#ef4444' },
    { name: 'N/A', value: answerStats.na, color: '#94a3b8' },
  ].filter(d => d.value > 0);

  const scoreColor = (s) => s >= 70 ? 'text-green-600' : s >= 40 ? 'text-amber-600' : 'text-red-600';
  const scoreBg = (s) => s >= 70 ? 'from-green-500 to-emerald-600' : s >= 40 ? 'from-amber-500 to-orange-600' : 'from-red-500 to-rose-600';
  const scoreLevel = (s) => s >= 80 ? { label: 'Excellent', icon: TrendingUp } : s >= 60 ? { label: 'Good', icon: TrendingUp } : s >= 40 ? { label: 'Fair', icon: Minus } : { label: 'Needs Improvement', icon: TrendingDown };

  const interp = scoreLevel(totalScore);
  const InterpIcon = interp.icon;

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-lg text-sm">
        <p className="font-medium text-gray-900">{payload[0].name || payload[0].payload?.name}</p>
        <p className="text-gray-600">{payload[0].value}%</p>
      </div>
    );
  };

  return (
    <div data-testid="review-step" className="max-w-5xl mx-auto space-y-6">
      {/* Company Info */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="text-sm font-medium text-gray-500 mb-3">Company Information</h3>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><span className="text-gray-400">Company:</span> <span className="text-gray-900 ml-1 font-medium">{company.company_name || '-'}</span></div>
          <div><span className="text-gray-400">Email:</span> <span className="text-gray-900 ml-1">{company.contact_email || '-'}</span></div>
        </div>
      </div>

      {/* Overall Score Hero */}
      <div className={`bg-gradient-to-br ${scoreBg(totalScore)} text-white rounded-2xl p-8 text-center shadow-lg`}>
        <p className="text-6xl font-bold mb-2">{totalScore.toFixed(1)}%</p>
        <div className="flex items-center justify-center gap-2 mb-1">
          <InterpIcon className="w-5 h-5" />
          <span className="text-xl font-semibold">{interp.label}</span>
        </div>
        <p className="text-white/80 text-sm">Overall Sustainability Score</p>
      </div>

      {/* Category Scores Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {Object.entries(CATEGORIES).map(([key, label]) => {
          const val = scores[key] || 0;
          return (
            <div key={key} className="bg-white border border-gray-200 rounded-xl p-4 text-center">
              <p className="text-xs text-gray-500 mb-2">{label}</p>
              <p className={`text-2xl font-bold ${scoreColor(val)}`}>{val.toFixed(1)}%</p>
              <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all duration-500" style={{ width: `${val}%`, backgroundColor: COLORS[key] }} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Charts Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Pie Chart - Answer Distribution */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h3 className="text-sm font-medium text-gray-900 mb-4">Answer Distribution</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={answerDistData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={3} dataKey="value"
                label={({ name, value }) => `${name}: ${value}`} labelLine={false}>
                {answerDistData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-4 mt-2">
            {answerDistData.map(d => (
              <div key={d.name} className="flex items-center gap-1.5 text-xs text-gray-600">
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.color }} />
                {d.name} ({d.value})
              </div>
            ))}
          </div>
        </div>

        {/* Radar Chart */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h3 className="text-sm font-medium text-gray-900 mb-4">Performance Radar</h3>
          <ResponsiveContainer width="100%" height={250}>
            <RadarChart data={radarData}>
              <PolarGrid stroke="#e5e7eb" />
              <PolarAngleAxis dataKey="category" tick={{ fontSize: 11, fill: '#6b7280' }} />
              <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 10, fill: '#9ca3af' }} />
              <Radar name="Score" dataKey="score" stroke="#10b981" fill="#10b981" fillOpacity={0.2} strokeWidth={2} />
              <Tooltip content={<CustomTooltip />} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Bar Chart - Category Comparison */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="text-sm font-medium text-gray-900 mb-4">Category Score Comparison</h3>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={barData} barSize={40}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
            <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#6b7280' }} axisLine={false} tickLine={false} />
            <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="score" radius={[6, 6, 0, 0]}>
              {barData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
          <CheckCircle className="w-8 h-8 text-green-600" />
          <div>
            <p className="text-2xl font-bold text-green-700">{answerStats.yes}</p>
            <p className="text-xs text-green-600">Fully Compliant</p>
          </div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
          <AlertTriangle className="w-8 h-8 text-amber-600" />
          <div>
            <p className="text-2xl font-bold text-amber-700">{answerStats.partial}</p>
            <p className="text-xs text-amber-600">Partially Compliant</p>
          </div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
          <XCircle className="w-8 h-8 text-red-600" />
          <div>
            <p className="text-2xl font-bold text-red-700">{answerStats.no}</p>
            <p className="text-xs text-red-600">Non-Compliant</p>
          </div>
        </div>
      </div>

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h3 className="text-sm font-medium text-gray-900 mb-3">Key Recommendations</h3>
          <div className="space-y-3">
            {recommendations.map((rec, i) => (
              <div key={i} className="bg-amber-50 border border-amber-100 rounded-lg p-3">
                <p className="text-xs text-amber-700 font-medium mb-1">{rec.question_text}</p>
                <p className="text-sm text-gray-800">{rec.recommendation}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Detailed Answers by Category */}
      {Object.entries(answeredByCategory).map(([cat, items]) => (
        <div key={cat} className="bg-white border border-gray-200 rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-900">{CATEGORIES[cat] || cat}</h3>
            <span className={`text-sm font-bold ${scoreColor(scores[cat] || 0)}`}>{(scores[cat] || 0).toFixed(1)}%</span>
          </div>
          <div className="space-y-2">
            {items.map((item, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                <p className="text-sm text-gray-800 flex-1 pr-4">{item.question.question_text}</p>
                <span className={`text-xs font-medium px-2.5 py-1 rounded-lg whitespace-nowrap ${
                  item.isNa ? 'bg-gray-100 text-gray-500'
                  : item.optionName.toLowerCase() === 'yes' ? 'bg-green-50 text-green-700'
                  : item.optionName.toLowerCase().includes('partial') ? 'bg-amber-50 text-amber-700'
                  : item.optionName.toLowerCase() === 'no' ? 'bg-red-50 text-red-700'
                  : 'bg-emerald-50 text-emerald-700'
                }`}>{item.optionName}</span>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* CTA */}
      <div className={`rounded-xl p-6 text-center ${allAnswered ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
        {!allAnswered ? (
          <>
            <h3 className="text-lg font-bold text-amber-800 mb-2">Incomplete Assessment</h3>
            <p className="text-amber-700 text-sm mb-4">
              You've answered {answeredCount} of {totalQuestions} questions. 
              Please go back and complete all questions before submitting.
            </p>
            <button data-testid="back-to-questionnaire-btn" onClick={() => window.history.back()}
              className="bg-amber-600 hover:bg-amber-700 text-white font-semibold px-8 py-3 rounded-xl transition-colors">
              Back to Questionnaire
            </button>
          </>
        ) : (
          <>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Ready to Submit?</h3>
            <p className="text-gray-500 text-sm mb-4">{submitLabel ? "Submit your assessment to your dashboard." : "Create an account to submit your assessment and track its progress"}</p>
            <button data-testid="proceed-to-register-btn" onClick={() => onNext(assessmentData)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-8 py-3 rounded-xl transition-colors">
              {submitLabel || "Register & Submit"}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default ReviewStep;
